import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { GenerationRequestDetailRead } from '../../types/api';
import { fetchGenerationRequest } from '../generation/generationApi';

export interface GenerationRequestResult {
  request: GenerationRequestDetailRead | null;
  isLoading: boolean;
  /** Load error only. Write errors are surfaced by the form that made the call. */
  error: string | null;
  /** The id is not this business's, or does not exist. */
  notFound: boolean;
  refresh: () => void;
  /** Replaces the cached request with a server response, e.g. after selecting. */
  applyRequest: (next: GenerationRequestDetailRead) => void;
}

/** Result of the last completed load, tagged with what it was loaded for. */
interface LoadedRequest {
  key: string;
  request: GenerationRequestDetailRead | null;
  error: string | null;
  notFound: boolean;
}

function loadKey(businessId: string, requestId: string): string {
  return `${businessId}/${requestId}`;
}

/**
 * One generation request with its variants and slides.
 *
 * Same shape as the knowledge-base loaders: the result is stored tagged with
 * the business + request it belongs to and the visible state is derived during
 * render, so nothing is written to state synchronously in the effect and
 * another request's variants can never flash on screen.
 */
export function useGenerationRequest(
  businessId: string | null,
  requestId: string | null,
): GenerationRequestResult {
  const [loaded, setLoaded] = useState<LoadedRequest | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  // A run changed elsewhere (the assistant edited a caption, a slide or the
  // look, or undid one): re-read when it is this run, or when unnamed.
  const refreshIfThisRun = useCallback(
    (changedId: string | null) => {
      if (changedId === null || changedId === requestId) refresh();
    },
    [refresh, requestId],
  );
  useRefreshOn(requestId ? businessId : null, 'runs', refreshIfThisRun);

  useEffect(() => {
    if (!businessId || !requestId) return;

    const key = loadKey(businessId, requestId);
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const request = await fetchGenerationRequest(businessId, requestId, controller.signal);
        if (cancelled) return;
        setLoaded({ key, request, error: null, notFound: false });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setLoaded({ key, request: null, error: null, notFound: true });
          return;
        }
        setLoaded({
          key,
          request: null,
          error: getErrorMessage(caught, 'Could not load that post.'),
          notFound: false,
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, requestId, nonce]);

  const applyRequest = useCallback(
    (next: GenerationRequestDetailRead) => {
      if (!businessId || !requestId) return;
      setLoaded({
        key: loadKey(businessId, requestId),
        request: next,
        error: null,
        notFound: false,
      });
    },
    [businessId, requestId],
  );

  const isCurrent =
    loaded !== null &&
    businessId !== null &&
    requestId !== null &&
    loaded.key === loadKey(businessId, requestId);

  return {
    request: isCurrent ? loaded.request : null,
    isLoading: businessId !== null && requestId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    notFound: isCurrent ? loaded.notFound : false,
    refresh,
    applyRequest,
  };
}
